import { hasProp } from "@@/utilities/has-prop";
import { isNullOrUndefined } from "@@/utilities/null-checks";
import isPlainObject from "lodash.isplainobject";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    isLanguageCode,
    languageCodeMap,
    languageCodes,
    type LanguageCode,
} from "./language-code";

type AllButSwedish = Exclude<LanguageCode, "sv">;

const translatableValueZodObject = z.object({
    _type: z.enum(["TRANSLATABLE_VALUE"]),
    isAutoTranslated: z
        .object({
            sv: z.boolean().nullish(),
            en: z.boolean().nullish(),
            de: z.boolean().nullish(),
        })
        .nullish(),
    sv: z.string(),
    en: z.string().nullish(),
    de: z.string().nullish(),
});

/**
 * Either a string in swedish (sv)
 * or an object with language codes as keys
 * and the string value in the language specified
 * (sv is the only required language)
 */
type TranslatableValue = z.infer<typeof translatableValueZodObject>;

const translatableValueCache = new Map<string, TranslatableValue>();

const translatableValueFactory = {
    from: (params: Except<TranslatableValue, "_type">): TranslatableValue => {
        // Generate a key quickly from the key value pairs of the translatable values
        // that are languageCode and string values, not other meta data
        const key = (
            Array.from(Object.entries(params)).filter(
                ([key, value]) =>
                    typeof value === "string" && isLanguageCode(key),
            ) as [LanguageCode, string][]
        )
            .map(([key, value]) => `${key}#${value}`)
            .join("#");

        const oldValue = translatableValueCache.get(key);
        // check cache for translatable value
        // prevents creating a new one if it's already been created
        const result = oldValue ?? { ...params, _type: "TRANSLATABLE_VALUE" };
        if (!oldValue) translatableValueCache.set(key, result);
        return result;
    },
    fromTranslatable: function (
        translatable: TranslatableType,
    ): TranslatableValue | undefined {
        if (typeof translatable === "string")
            return translatableValueFactory.from({ sv: translatable });
        if (isTranslatableValue(translatable)) return translatable;
        return undefined;
    },
    fromTranslatableType: function (translatable: TranslatableType) {
        if (isTranslatableValue(translatable)) return translatable;
        if (typeof translatable === "string")
            return translatableValueFactory.from({ sv: translatable });
        if (isTranslatableValueFactoryParams(translatable))
            return translatableValueFactory.from(translatable);
        return translatableValueFactory.from({ sv: "" });
    },
};
type TranslatableValueFactory = (
    params: Except<TranslatableValue, "_type">,
) => TranslatableValue;
type TranslatableValueFactoryParams = Parameters<TranslatableValueFactory>[0];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTranslatableValue = (item: any): item is TranslatableValue =>
    item?._type === "TRANSLATABLE_VALUE";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTranslatableValueReq = (item: any): item is TranslatableValue_Req =>
    item?._type === "TRANSLATABLE_VALUE" && item.sv?.trim().length > 0;

type TranslatableType =
    | RequiredTranslatable
    | Translatable
    | Translatable_Req
    | TranslatableValueFactoryParams;
type NonNullableNonStringTranslatableType = Exclude<
    TranslatableType,
    null | string | undefined
>;

const areTranslatablesEqual = (
    t1: TranslatableType | undefined,
    t2: TranslatableType | undefined,
) => {
    if (t1 === null) return false;
    if (t2 === null) return false;
    if (typeof t1 === "undefined") return false;
    if (typeof t2 === "undefined") return false;
    if (typeof t1 !== typeof t2) return false;
    if (typeof t1 === "string") return t1 === t2;
    if (typeof t2 === "string") return false; // just to make ts happy

    // get the keys of both objects
    const t1_keys = Object.keys(t1);
    const t2_keys = Object.keys(t2);

    // Check if they have the same number of keys
    if (t1_keys.length !== t2_keys.length) return false;
    if (t1_keys.length === 0) return true;

    return t1_keys.every(key => {
        // make sure the key exists in both objects
        if (!hasProp(t2, key)) return false;
        // get the values from both objects
        const value1 = t1[key as keyof NonNullableNonStringTranslatableType];
        const value2 = t2[key as keyof NonNullableNonStringTranslatableType];
        // we only care about strings at this stage
        if (typeof value1 !== "string") return true;
        if (typeof value2 !== "string") return true;
        // compare the string values
        return value1 === value2;
    });
};

type TranslatableString = `#T#${string}`;
const translatableStringFactory = (
    input: NonNullable<Translatable | TranslatableValueFactoryParams>,
): TranslatableString => {
    if (typeof input === "string")
        return `#T#${JSON.stringify(translatableValueFactory.from({ sv: input }))}`;
    if (isTranslatableValue(input)) return `#T#${JSON.stringify(input)}`;
    return `#T#${JSON.stringify(translatableValueFactory.from(input))}`;
};

const fromTranslatableString = (
    input: TranslatableString | string,
): Translatable => {
    if (input.startsWith("#T#"))
        return JSON.parse(input.slice(3)) as TranslatableValue;
    return input;
};

const isTranslatableString = (input: unknown): input is TranslatableString => {
    if (typeof input === "string" && input.startsWith("#T#")) return true;
    return false;
};

const translatableStringUtils = {
    translatableStringFactory,
    fromTranslatableString,
    isTranslatableString,
};

/** Same as `translatableValueZodSchema` although doesn't accept empty string */
const translatableValueReqZodObject = z
    .object({
        _type: z.enum(["TRANSLATABLE_VALUE"]),
        isAutoTranslated: z
            .object({
                sv: z.boolean().nullish(),
                en: z.boolean().nullish(),
                de: z.boolean().nullish(),
            })
            .nullish(),
        sv: z.string(),
        en: z.string().nullish(),
        de: z.string().nullish(),
    })
    .refine(
        value => {
            if (!value?.sv || value?.sv?.trim() === "") return false;
            return true;
        },
        translatableStringFactory({
            sv: "Textfältet saknar värde på svenska",
            en: "The text field is missing a value in Swedish",
            de: "Das Textfeld fehlt ein Wert auf Schwedisch",
        }),
    );

/**
 * Same as `TranslatableValue` although doesn't
 * accept empty string
 *
 * Either a string in swedish (sv)
 * or an object with language codes as keys
 * and the string value in the language specified
 * (sv is the only required language)
 */
type TranslatableValue_Req = z.infer<typeof translatableValueReqZodObject>;

/**
 * If you are using this you
 * probably want to use `TranslatableReq` instead
 * since it checks for empty string values as well
 * @deprecated
 */
type RequiredTranslatable = NonNullable<Translatable>;

const isTranslatableValueFactoryParams = (
    item: unknown,
): item is TranslatableValueFactoryParams => {
    if (isPlainObject(item)) {
        const keys = Object.keys(item as Record<string, unknown>);
        if (keys.includes("_type")) return false;
        const hasSwedish = keys.includes("sv");
        return hasSwedish;
    }
    return false;
};

const translatedFactory = (
    input: Except<TranslatableValue, "_type">,
): RequiredTranslatable => {
    return {
        ...input,
        _type: "TRANSLATABLE_VALUE",
    };
};

type Translatable = string | null | TranslatableValue;

const translatableReqZodUnion = z.union([
    translatableValueReqZodObject,
    z.string().trim().min(1),
]);

type Translatable_Req = z.infer<typeof translatableReqZodUnion>;

const translatableZodSchema: z.ZodSchema<Translatable> = z.union(
    [z.string().nullable(), translatableValueZodObject],
    {
        errorMap: (issue, ctx) => {
            if (issue.code === "invalid_union") {
                const issues = issue.unionErrors.flatMap(error => error.issues);
                const inEmpty = issues.every(
                    issue =>
                        issue.code === "invalid_type" &&
                        (issue.expected === "undefined" ||
                            issue.expected === "null"),
                );
                if (inEmpty)
                    return {
                        message: translatableStringFactory({
                            sv: "Obligatoriskt fält",
                            en: "Required field",
                        }),
                    };
            }

            return { message: ctx.defaultError };
        },
    },
);

/**
 * Checks if value is a valid Translatable value
 * @param {*} item
 * @returns {item is Translatable}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTranslatable = (item: any): item is Translatable =>
    [
        isTranslatableValue,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (_item: any) => _item === null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (_item: any) => typeof _item === "string",
    ].some(check => check(item));
/**
 * Checks if value is a valid Translatable value
 * @param {*} item
 * @returns {item is Translatable}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isTranslatableReq = (item: any): item is Translatable_Req =>
    [
        isTranslatableValueReq,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (_item: any) => typeof _item === "string" && _item.trim().length > 0,
    ].some(check => check(item));

type TranslationKey = string;
type Translation = {
    [swedish: string]: Translatable;
};

function valueTranslator(
    language: LanguageCode,
    ignoreLanguageFallbacks = false,
    /** At the moment "sv" is always available even if it's left out */
    availableLanguages: LanguageCode[] | readonly LanguageCode[] = [
        ...languageCodes,
    ],
) {
    const invalidLanguage = !availableLanguages.includes(language);
    const _swedishIsAvailable = availableLanguages.includes("sv");
    const englishIsAvailable = availableLanguages.includes("en");

    function translate<T extends Translatable | undefined>(
        item: T,
    ): T extends undefined | "" ? undefined : string;
    function translate(item: Translatable | undefined): undefined | string {
        const _item =
            typeof item === "string"
                ? item.startsWith("#T#")
                    ? // Support for translated strings
                      (JSON.parse(item.slice(3)) as Translatable)
                    : item
                : item;

        // If no translations, return string value
        if (typeof _item === "undefined") return undefined;
        if (_item === null) return undefined;
        if (typeof _item === "string") {
            return !_item.trim() ? "" : _item;
        }

        // If swedish is requested, return the swedish value
        if (language === "sv") return _item.sv;
        // If fallbacks are ignored, return the requested language no matter what
        if (ignoreLanguageFallbacks) return _item[language] || undefined;
        // If english is requested, return the english value
        // but fallback to the swedish value if english is not available
        if (language === "en") return _item.en || _item.sv;
        // If any other language is requested, return the requested language
        // but fallback to the en value if requested language is not available
        if (invalidLanguage)
            return (englishIsAvailable && _item.en) || _item.sv;
        return _item[language] || (englishIsAvailable && _item.en) || _item.sv;
    }

    return translate;
}

type ValueTranslator = ReturnType<typeof valueTranslator>;

type Translated<P> = {
    [key in keyof P]: P[key] extends string
        ? string
        : P[key] extends TranslatableValue | undefined
          ? Text
          : P[key];
};

function objectTranslator(language: LanguageCode) {
    function translate<T extends Record<string, unknown> | undefined>(
        input: T,
    ): T extends undefined ? undefined : Translated<T>;
    function translate<T extends Record<string, unknown>>(
        input: T | undefined,
    ): Translated<T> | undefined {
        if (isNullOrUndefined(input)) {
            return undefined;
        }
        const translate = valueTranslator(language);
        const output: Translated<T> = Object.entries(input).reduce(
            (agg, [propKey, propValue]) => {
                if (typeof propValue === "string") {
                    agg[propKey] = propValue;
                    return agg;
                }
                if (
                    isPlainObject(propValue) &&
                    hasProp(propValue as Record<string, unknown>, "_type")
                ) {
                    const type = (propValue as { _type: string })._type;
                    switch (type) {
                        // case "TEXT_STYLED":
                        case "TEXT":
                            agg[propKey] = propValue;
                            return agg;
                        // case "TRANSLATABLE_VALUE_STYLED":
                        case "TRANSLATABLE_VALUE": {
                            agg[propKey] = translate(propValue as Translatable);
                            return agg;
                        }
                    }
                }
                agg[propKey] = propValue;
                return agg;
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {} as any,
        );

        return output;
    }

    return translate;
}

/**
 * Translates keys to values using translations provided
 * in `source` and by the language code.
 * Default language is browser language, if translation
 * for language is not available it returns the key
 * untranslated
 *
 * @param {Map<TranslationKey, TranslatableValue>} translations
 * @param {LanguageCode} [language=defaultLanguage]
 */
const createTranslator =
    (language: LanguageCode) =>
    (translations: Map<TranslationKey, Translatable>) =>
    (key: string): string => {
        // get value to translate, ignore case
        const translationKey = key.toLowerCase();
        const translationValue = translations.get(translationKey);
        // if no translation exists, return original key
        if (!translationValue) return key;

        // get translated value (if any)
        const output = valueTranslator(language)(translationValue);
        return output || key;
    };

const asTranslationMap = (
    translation: Translation,
): Map<TranslationKey, Translatable> => {
    return new Map(Object.entries(translation));
};

const svTranslator = valueTranslator(languageCodeMap.sv);
const enTranslator = valueTranslator(languageCodeMap.en);
const deTranslator = valueTranslator(languageCodeMap.de);

const asTranslatableValue = (
    value: Translatable | undefined,
): TranslatableValue => {
    if (isTranslatableValue(value)) return value;
    if (typeof value === "string")
        return translatableValueFactory.from({ sv: value });
    return translatableValueFactory.from({ sv: "" });
};

const getTranslationValuesAsString = (value: Translatable): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return (
        Object.entries(value).filter(
            ([key, val]) =>
                typeof val === "string" &&
                languageCodes.some(item => item === key),
        ) as [LanguageCode, string][]
    )
        .map(([key, val]) => `${key}:${val}`)
        .join(",");
};

const emptyTranslationFactory = (): RequiredTranslatable => {
    return translatedFactory({ sv: "" });
};

/**
 * Compares two translatables and check if their values match. \
 * NOTE: If they both are null or undefined, they match.
 *
 * @param t1 - The first translatable to compare.
 * @param t2 - The second translatable to compare.
 * @returns True if the translatables match, false otherwise.
 */
const compareTranslatables = (
    t1: Translatable | undefined,
    t2: Translatable | undefined,
): boolean => {
    // Compare two translatables and check if they
    // match, return true if they match
    if (t1 === null || typeof t1 === "undefined") {
        // Check if old and new are both undefined
        return t2 === null || typeof t2 === "undefined";
    }
    if (typeof t1 === "string") {
        // Check if old value is a string
        if (typeof t2 === "string") {
            // And check if so make sure they don't
            // match each other
            return t1 === t2;
        }
        // Both of them are not strings
        // so we know they are different
        return false;
    }

    // Now we know t1 value is a translatable
    // So let's check if the t2 is a translatable too
    if (isTranslatableValue(t2)) {
        // If the t2 is a translatable value
        // check if their values match
        return Object.entries(t1).every(([key, value]) => {
            return value === t2[key as keyof TranslatableValue];
        });
    }

    // Now we know they are not the same
    return false;
};

const requiredTranslatableZodSchema: z.ZodSchema<RequiredTranslatable> =
    z.union([z.string(), translatableValueZodObject], {
        errorMap: (issue, ctx) => {
            if (issue.code === "invalid_union") {
                const issues = issue.unionErrors.flatMap(error => error.issues);
                const inEmpty = issues.every(
                    issue =>
                        issue.code === "invalid_type" &&
                        (issue.received === "undefined" ||
                            issue.received === "null"),
                );
                if (inEmpty)
                    return {
                        message: translatableStringFactory({
                            sv: "Obligatoriskt fält",
                            en: "Required field",
                        }),
                    };
            }

            return { message: ctx.defaultError };
        },
    });
// TODO! Enable below when we have the time to handle the fallout on old
// TODO! forms where we depend on letting empty strings pass through
// TODO! https://github.com/hej-alfred/towni/issues/4407
// .refine(
//     value => {
//         if (value === null) return false;
//         if (typeof value === "string") return value.trim().length > 0;
//         return value.sv.trim().length > 0;
//     },
//     value => ({
//         message: translatableStringFactory({
//             sv: "Obligatoriskt fält",
//             en: "Required field",
//         }),
//         params: { value },
//     }),
// );

function translation(
    params: Except<TranslatableValue, "_type">,
    outputAs: "string",
): TranslatableString | string;
function translation(
    params: Except<TranslatableValue, "_type">,
    outputAs?: "translatableValue",
): TranslatableValue;
function translation(
    params: Except<TranslatableValue, "_type">,
    outputAs?: "string" | "translatableValue",
): TranslatableValue | TranslatableString | string {
    if (outputAs === "string") {
        // Convert it to a `TranslatableString`
        return translatableStringFactory(translatableValueFactory.from(params));
    }
    // Convert it to a `TranslatableValue`
    return translatableValueFactory.from(params);
}

type UseTranslateTextInput =
    | Translatable
    | TranslatableString
    | TranslatableValueFactoryParams
    | undefined;
type UseTranslateResult = (
    text: UseTranslateTextInput,
    language?: LanguageCode,
    ignoreLanguageFallbacks?: boolean,
) => string;

export {
    fromTranslatableString,
    isTranslatableString,
    translatableStringFactory as toTranslatableString,
    translatableStringFactory,
    translatableStringUtils,
};

export type { TranslatableString, UseTranslateResult, UseTranslateTextInput };

export {
    areTranslatablesEqual,
    asTranslatableValue,
    asTranslationMap,
    compareTranslatables,
    createTranslator,
    deTranslator,
    emptyTranslationFactory,
    enTranslator,
    getTranslationValuesAsString,
    isTranslatable,
    isTranslatableReq,
    isTranslatableValue,
    isTranslatableValueFactoryParams,
    objectTranslator,
    requiredTranslatableZodSchema,
    svTranslator,
    translatableReqZodUnion,
    translatableValueFactory,
    translatableValueReqZodObject,
    translatableValueZodObject,
    translatableZodSchema,
    translatedFactory,
    translation,
    valueTranslator,
};
export type {
    AllButSwedish,
    RequiredTranslatable,
    Translatable,
    Translatable_Req,
    TranslatableType,
    TranslatableValue,
    TranslatableValue_Req,
    TranslatableValueFactory,
    TranslatableValueFactoryParams,
    Translated,
    Translation,
    TranslationKey,
    ValueTranslator,
};
